import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { of } from 'rxjs';
import { filter, catchError, tap, map, switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { Utils } from '../resources/utils';
import { Cargo } from '../interfaces/cargo';
import { LocationAddress } from '../interfaces/locationAddress';
import { CargoRoute } from '../interfaces/cargoRoute';
declare var google: any;

@Injectable()
export class GoogleService {
    private geocoder: any;

    constructor(
        public utils: Utils
    ) { }

    public getRouteDataCargo(cargo, travelMode?: string): Promise<{
        cargoDistancy: number,
        cargoEstimatedTime: number,
        cargoRoute: CargoRoute[]
    }> {
        const promise = new Promise<{
            cargoDistancy: number,
            cargoEstimatedTime: number,
            cargoRoute: CargoRoute[]
        }>((resolve, reject) => {
            const locationsList = this.utils.getListGroupLocationsCargo(cargo);
            const directionsService = new google.maps.DirectionsService();
            let count = 1;
            let distancy = 0;
            let estimatedTime = 0;
            let responseDirections: CargoRoute[] = [];

            for (const value of locationsList) {
                directionsService.route({
                    origin: value.origin,
                    destination: value.destination,
                    waypoints: value.waypoints,
                    travelMode: (travelMode || 'DRIVING')
                }, (response, status) => {
                    if (status !== 'OK') reject();
                    if (!response || !response.routes || !response.routes.length) reject();
                    responseDirections.push(response);
                    if (!response.routes[0] || !response.routes[0].legs || !response.routes[0].legs.length) reject();
                    distancy += response.routes[0].legs[0].distance.value;
                    estimatedTime += response.routes[0].legs[0].duration.value;
                    if (count === locationsList.length) {
                        resolve({
                            cargoDistancy: distancy,
                            cargoEstimatedTime: estimatedTime,
                            cargoRoute: responseDirections
                        });
                    }
                    count++;
                });
            }
        });
        return promise;
    }

    public getRouteData(locationsList: Array<any>, travelMode?: string, alternatives?: boolean) {
        const promise = new Promise((resolve, reject) => {
            const directionsService = new google.maps.DirectionsService();
            const points = this.utils.getWaypointsObject(locationsList);

            directionsService.route({
                origin: points.origin,
                destination: points.destination,
                waypoints: points.waypoints,
                travelMode: (travelMode || 'DRIVING'),
                provideRouteAlternatives: alternatives
            }, (response, status) => {
                if (status === 'OK') {
                    if (response.routes.length) {
                        if (response.routes[0].legs.length) {
                            resolve({
                                cargoDistancy: response.routes[0].legs[0].distance.value,
                                cargoEstimatedTime: response.routes[0].legs[0].duration.value,
                                cargoRoute: response
                            });
                        } else {
                            reject(new Error('La ruta no contiene segmentos válidos'));
                        }
                    } else {
                        reject(new Error('No se pudo encontrar un punto entre las direcciones especificadas.'));
                    }
                } else {
                    let errorMessage = 'Error al calcular la ruta';
                    switch (status) {
                        case 'NOT_FOUND':
                            errorMessage = 'Una o más ubicaciones no pudieron ser encontradas';
                            break;
                        case 'ZERO_RESULTS':
                            errorMessage = 'No se encontraron rutas entre las ubicaciones especificadas.';
                            break;
                        case 'OVER_QUERY_LIMIT':
                            errorMessage = 'Se ha excedido el límite de consultas a Google Maps';
                            break;
                        case 'REQUEST_DENIED':
                            errorMessage = 'La solicitud fue denegada por Google Maps';
                            break;
                        case 'INVALID_REQUEST':
                            errorMessage = 'La solicitud contiene parámetros inválidos';
                            break;
                        case 'UNKNOWN_ERROR':
                            errorMessage = 'Error desconocido en el servidor de Google Maps';
                            break;
                        default:
                            errorMessage = `Error de Google Maps: ${status}`;
                    }
                    reject(new Error(errorMessage));
                }
            });

        });
        return promise;
    }

    public getOptimizedRouteData(locationsList: Array<any>, travelMode?: string): Promise<{
        cargoDistancy: number,
        cargoEstimatedTime: number,
        cargoRoute: google.maps.DirectionsResult,
        waypointOrder: number[]
    }> {
        const promise = new Promise<{
            cargoDistancy: number,
            cargoEstimatedTime: number,
            cargoRoute: google.maps.DirectionsResult,
            waypointOrder: number[]
        }>((resolve, reject) => {
            if (!locationsList || locationsList.length < 2)
                return reject(new Error('Debe indicar al menos dos ubicaciones para calcular una ruta'));

            const directionsService = new google.maps.DirectionsService();
            const origin = locationsList[0];
            const destination = locationsList[locationsList.length - 1];
            const waypoints = [];

            for (let i = 1; i < locationsList.length - 1; i++) {
                waypoints.push({
                    location: locationsList[i],
                    stopover: true
                });
            }

            directionsService.route({
                origin,
                destination,
                waypoints,
                travelMode: (travelMode || 'DRIVING'),
                optimizeWaypoints: true,
                provideRouteAlternatives: false
            }, (response, status) => {
                if (status !== 'OK') {
                    let errorMessage = 'Error al calcular la ruta optimizada';
                    switch (status) {
                        case 'NOT_FOUND':
                            errorMessage = 'Una o más ubicaciones no pudieron ser encontradas';
                            break;
                        case 'ZERO_RESULTS':
                            errorMessage = 'No se encontraron rutas entre las ubicaciones especificadas.';
                            break;
                        case 'OVER_QUERY_LIMIT':
                            errorMessage = 'Se ha excedido el límite de consultas a Google Maps';
                            break;
                        case 'REQUEST_DENIED':
                            errorMessage = 'La solicitud fue denegada por Google Maps';
                            break;
                        case 'INVALID_REQUEST':
                            errorMessage = 'La solicitud contiene parámetros inválidos';
                            break;
                        case 'UNKNOWN_ERROR':
                            errorMessage = 'Error desconocido en el servidor de Google Maps';
                            break;
                        default:
                            errorMessage = `Error de Google Maps: ${status}`;
                    }
                    return reject(new Error(errorMessage));
                }

                if (!response || !response.routes || !response.routes.length)
                    return reject(new Error('No se encontró una ruta optimizada válida'));

                const route = response.routes[0];
                if (!route.legs || !route.legs.length)
                    return reject(new Error('La ruta optimizada no contiene segmentos válidos'));

                const totals = route.legs.reduce((acc, leg) => {
                    acc.distance += leg.distance && leg.distance.value ? leg.distance.value : 0;
                    acc.duration += leg.duration && leg.duration.value ? leg.duration.value : 0;
                    return acc;
                }, { distance: 0, duration: 0 });

                resolve({
                    cargoDistancy: totals.distance,
                    cargoEstimatedTime: totals.duration,
                    cargoRoute: response,
                    waypointOrder: route.waypoint_order || []
                });
            });
        });

        return promise;
    }

    public calculateRouteCargo(cargo: Cargo, travelMode?: string) {
        const promise = new Promise((resolve, reject) => {

            const locationsList = this.utils.getListGroupLocationsCargo(cargo);
            const directionsService = new google.maps.DirectionsService();
            let count = 1;
            let responseDirections = [];

            for (const value of locationsList) {
                directionsService.route({
                    origin: value.origin,
                    destination: value.destination,
                    waypoints: value.waypoints,
                    travelMode: (travelMode || 'DRIVING')
                }, (response, status) => {
                    if (status === 'OK') {
                        if (response.routes.length) {
                            responseDirections.push(response);
                            if (count === locationsList.length) {
                                resolve(responseDirections);
                            }
                            count++;
                        }
                    }
                });
            }
        });
        return promise;
    }

    public createPath(locations: any) {
        const cargoPath = locations.map((location) => {
            let obj: { lat, lng, contentInfoWindow?} = {
                lat: location.latitude,
                lng: location.longitude
            };
            if (location.contentInfoWindow) {
                obj.contentInfoWindow = location.contentInfoWindow;
            }
            return obj;
        });
        return cargoPath;
    }

    public calculateMinRouteTime(origin: LocationAddress, destination: LocationAddress, callback: any, waypoints = [], travelMode: string = 'DRIVING') {
        const directionsService = new google.maps.DirectionsService();

        directionsService.route({
            origin: origin,
            destination: destination,
            waypoints: waypoints,
            travelMode: travelMode
        }, callback);
    }
}